import { useAberturaListaCpf } from '@/hooks/useVendaIaCpf';
import { Bloco, Par, Pares, Tile, Tiles, moeda, telefoneFmt } from './lead-cpf-blocos';

/**
 * A ABERTURA DA OI E DA ALGAR NO LEAD (Douglas 02/10/2026): "isso tudo vai estar
 * no lead? ... quais são os telefones que vamos disparar?". O que o disparo manda,
 * para qual telefone e com que oferta — lido da mesma régua do disparo
 * (fn_venda_ia_cpf_abertura_lista). O texto é o mesmo da Apresentação da aba Proposta.
 */
const PRACA: Record<string, string> = {
  MD1: 'Mercado em Desenvolvimento 1', MD2: 'Mercado em Desenvolvimento 2',
  ESPECIAL: 'Especial', ESPECIAL_PLUS: 'Especial+', GM: 'Grande Mercado (Padrão)',
};
const OPERADORA: Record<string, string> = { CLARO: 'Claro', VIVO: 'Vivo', TIM: 'TIM', ALGAR: 'Algar' };
const MOTIVO_ALGAR: Record<string, string> = {
  PEDIDO_ABERTO: 'pedido aberto', OUTRO: 'pedido cancelado', CANCELAMENTO: 'ligou para cancelar', TECNICO: 'problema técnico',
};

export function LeadAberturaListaCpf({ leadId, oi, rf = false }: { leadId: string; oi: boolean; rf?: boolean }) {
  const { data: a, isLoading, error } = useAberturaListaCpf(leadId);
  if (isLoading) return null;
  if (error) {
    return <p role="alert" className="text-sm text-destructive">Não foi possível carregar a abertura deste lead. Feche o lead e abra de novo.</p>;
  }
  if (!a) return null;

  return (
    <Bloco
      titulo={`Venda IA CPF · ${a.top ? `TOP ${a.top}` : 'fora dos TOPs'}`}
      meta={`${PRACA[a.grupo ?? ''] ?? 'praça não encontrada'}${a.campanha ? ` · ${a.campanha}` : ''}`}
    >
      <Tiles>
        {rf
          ? <Tile rotulo={`celular ${a.celular?.nome ?? ''}`.trim()} valor={moeda(a.celular?.valor ?? null)} />
          : oi
          ? <Tile rotulo="paga hoje de internet na Oi" valor={moeda(a.valor_oi ?? null)} />
          : <Tile rotulo="internet hoje na Algar" valor={a.velocidade_hoje ?? '—'} />}
        <Tile
          rotulo={a.fibra ? `fibra Claro ${a.fibra.internet}` : 'fibra Claro'}
          valor={a.fibra ? moeda(a.fibra.valor) : '—'}
          sufixo={a.fibra?.meses ? `por ${a.fibra.meses} meses` : undefined}
        />
        {a.celular
          ? <Tile rotulo={rf ? 'casa + celular' : `com o celular ${a.celular.nome}`} valor={moeda(a.total ?? null)} />
          : a.economia ? <Tile rotulo="a menos por mês, no mínimo" valor={moeda(a.economia)} /> : null}
      </Tiles>
      {a.modelo ? (
        <div className="space-y-1.5">
          <p className="text-[12.5px]">
            <span className="font-semibold">Mensagem do disparo:</span>{' '}
            <span className="text-muted-foreground">{a.rotulo}</span>
          </p>
          {a.texto ? (
            <p className="whitespace-pre-line rounded-lg border border-border bg-[#FAF8F4] p-3 text-[13px] leading-relaxed dark:bg-[#241A1A]">
              {a.texto}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="border-l-[3px] border-l-primary pl-2.5 text-[12.5px]">
          <span className="font-semibold">Fora do disparo:</span> {a.fora}.
        </p>
      )}
      <Pares>
        <Par rotulo="primeiro celular do disparo" valor={a.telefone ? telefoneFmt(a.telefone) : '—'} />
        <Par rotulo="operadora do celular" valor={OPERADORA[a.operadora ?? ''] ?? a.operadora_consultada ?? 'não consultada'} />
        {!oi && !rf && <Par rotulo="motivo na Algar" valor={MOTIVO_ALGAR[a.intencao ?? ''] ?? '—'} />}
        {a.fibra?.meses ? <Par rotulo="internet depois da promoção" valor={moeda(a.fibra.valor_depois)} /> : null}
      </Pares>
      <div className="text-[12.5px]">
        <span className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">celulares da lista, na ordem</span>
        {a.numeros?.length ? (
          <ol className="mt-1 space-y-0.5">
            {a.numeros.map((n, i) => (
              <li key={n.numero} className="grid grid-cols-[18px_minmax(0,1fr)_auto] gap-2">
                <span className="font-display tabular-nums text-muted-foreground">{i + 1}</span>
                <span className="font-display tabular-nums">{telefoneFmt(n.numero)}</span>
                <span className="text-muted-foreground">{OPERADORA[n.operadora] ?? n.operadora}</span>
              </li>
            ))}
          </ol>
        ) : <p className="mt-1 text-muted-foreground">nenhum celular com a operadora consultada</p>}
        {(a.celulares ?? 0) > (a.numeros?.length ?? 0) && (
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            {(a.celulares ?? 0) - (a.numeros?.length ?? 0)} celular(es) ainda sem a operadora consultada (ou de operadora pequena): entram quando a consulta voltar.
          </p>
        )}
        {(a.fixos ?? 0) > 0 && <p className="mt-0.5 text-[11.5px] text-muted-foreground">{a.fixos} telefone(s) fixo(s) da lista: nunca recebem.</p>}
      </div>
      <p className="text-[11px] text-muted-foreground">
        O disparo confere o WhatsApp na hora, nessa ordem, e manda no primeiro que tem; a mensagem é a da operadora daquele número (Claro ou outra). Sem nenhum com WhatsApp, o lead vai para NEGADO. Os preços saem do catálogo CPF, pela praça da cidade.
      </p>
    </Bloco>
  );
}
